import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import PrioritizeDetailModal, {
  PrioritizeItem,
  FeatureStatus,
  HealthState,
  ReleaseGroupSymbol,
} from '../../components/PrioritizeDetailModal';
import AddLinkedFeaturePopup from '../../components/AddLinkedFeaturePopup';
import BoardControlsButton, { BoardPrefs, loadBoardPrefs, isColumnVisible } from '../../components/BoardControlsButton';
import { prioritizeService } from '../../services/prioritizeService';
import { organizationService, OrgMember } from '../../services/organizationService';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useActiveProject } from '../../hooks/useActiveProject';
import { useFeatureLinkData } from '../../hooks/useFeatureLinkData';
import '../../styles/Pages.css';
import '../../styles/CustomersList.css';
import '../../styles/InsightsPage.css';
import '../../styles/PrioritizeModal.css';
import '../../styles/InitiativesPage.css';
import '../../styles/ReleasesPage.css';

/* ── Shared style maps ────────────────────────────────────────── */
const STATUS_PILL_STYLE: Record<FeatureStatus, { bg: string; fg: string; label: string }> = {
  'new-idea':    { bg: '#ccfbf1', fg: '#115e59', label: 'New idea' },
  'candidate':   { bg: '#f3e8ff', fg: '#6b21a8', label: 'Candidate' },
  'planned':     { bg: '#dbeafe', fg: '#1e40af', label: 'Planned' },
  'in-progress': { bg: '#ECEAFF', fg: '#432EA3', label: 'In Progress' },
  'released':    { bg: '#d1fae5', fg: '#047857', label: 'Released' },
  'backlog':     { bg: '#f3f4f6', fg: '#4b5563', label: 'Backlog' },
  'done':        { bg: '#d1fae5', fg: '#047857', label: 'Done' },
  'upcoming':    { bg: '#dbeafe', fg: '#1e40af', label: 'Upcoming' },
  'completed':   { bg: '#d1fae5', fg: '#047857', label: 'Completed' },
};
// Releases default to an "Upcoming" pill when they have no feature status.
const HEALTH_STYLE: Record<HealthState, { color: string; label: string }> = {
  'on-track':  { color: '#10B981', label: 'On track' },
  'at-risk':   { color: '#F97316', label: 'At risk' },
  'off-track': { color: '#EF4444', label: 'Off track' },
  'none':      { color: '#9ca3af', label: '—' },
};

// Health updates store an icon name ('up' | 'wave' | 'down'); map it to a HealthState.
const HEALTH_ICON_TO_STATE: Record<string, HealthState> = {
  up: 'on-track', wave: 'at-risk', down: 'off-track',
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const formatDateRange = (start?: string, end?: string): string => {
  const fmt = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const s = fmt(start), e = fmt(end);
  if (s && e) return `${s} - ${e}`;
  return s || e;
};

const initialsOf = (name?: string | null) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

type ColKey = 'timeframe' | 'team' | 'score' | 'owner' | 'hierarchy' | 'status' | 'health' | 'progress';
const COLUMNS: { key: ColKey; label: string; width: number }[] = [
  { key: 'timeframe', label: 'Timeframe',        width: 200 },
  { key: 'team',      label: 'Team',             width: 110 },
  { key: 'score',     label: 'Customer Score',   width: 110 },
  { key: 'owner',     label: 'Owner',            width: 110 },
  { key: 'hierarchy', label: 'Hierarchy parent', width: 200 },
  { key: 'status',    label: 'Status',           width: 130 },
  { key: 'health',    label: 'Health',           width: 150 },
  { key: 'progress',  label: '% Work progress',  width: 160 },
];
// Release-groups list shows a reduced set (no timeframe/team/hierarchy/status/health).
const GROUP_COLUMNS = COLUMNS.filter(c => c.key === 'score' || c.key === 'owner' || c.key === 'progress');

/* ── Markers ──────────────────────────────────────────────────── */
const FlagMarker: React.FC<{ color?: string }> = ({ color = '#f59e0b' }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={color} stroke="none"><path d="M5 3v18l7-5 7 5V3z" /></svg>
);
const SquareMarker: React.FC<{ color?: string }> = ({ color = 'var(--brand-500)' }) => (
  <span className="ini-sq" style={{ background: color }} />
);
const ProductIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/></svg>
);
const ComponentIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
);

/* ── Page ─────────────────────────────────────────────────────── */
const Releases: React.FC = () => {
  const { currentOrg } = useAuth();
  const { can } = usePermissions();
  const canManage = can('product_releases');
  const { activeProjectId } = useActiveProject();
  const { companies, customerUsers, documents } = useFeatureLinkData(currentOrg?.id, activeProjectId);

  const [items, setItems] = useState<PrioritizeItem[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<PrioritizeItem | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pickerFor, setPickerFor] = useState<{ id: string; top: number; left: number } | null>(null);
  const [tab, setTab] = useState<'releases' | 'groups'>('releases');
  const [boardPrefs, setBoardPrefs] = useState<BoardPrefs>(() => loadBoardPrefs('releases:boardControls'));
  // Board controls apply to the Releases tab's feature columns.
  const visibleReleaseCols = COLUMNS.filter(c => isColumnVisible(boardPrefs.columns, c.key));
  const activeColumns = tab === 'releases' ? visibleReleaseCols : GROUP_COLUMNS;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentOrg?.id) return;
      try {
        const rows = await prioritizeService.getItems(activeProjectId);
        if (!cancelled) setItems(rows);
      } catch (err) {
        console.error('Failed to load releases:', err);
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id, activeProjectId]);

  useEffect(() => {
    if (!currentOrg?.id) return;
    organizationService.getMembersWithProfiles(currentOrg.id).then(setMembers).catch(() => {});
  }, [currentOrg?.id]);

  const memberById = (id?: string | null) => id ? members.find(m => m.user_id === id) ?? null : null;
  const itemById = (id: string) => items.find(i => i.id === id);

  // Make a name unique among items of a kind by appending " 1", " 2", … if needed.
  const uniqueName = (base: string, kind: PrioritizeItem['kind'], excludeId?: string): string => {
    const taken = new Set(
      items.filter(i => i.kind === kind && i.id !== excludeId).map(i => i.name.trim().toLowerCase()),
    );
    if (!taken.has(base.trim().toLowerCase())) return base;
    let n = 1;
    while (taken.has(`${base} ${n}`.trim().toLowerCase())) n++;
    return `${base} ${n}`;
  };

  const q = search.trim().toLowerCase();
  const releases = useMemo(
    () => items.filter(i => i.kind === 'release' && (!q || i.name.toLowerCase().includes(q))),
    [items, q],
  );
  const featuresOfRelease = (releaseId: string) => items.filter(i => i.kind === 'feature' && i.releaseParentId === releaseId);

  // Work-progress for a release: manual value, or auto-rollup from linked features'
  // status completeness (matches the modal's auto-roll up logic).
  const FEATURE_COMPLETENESS: Record<string, number> = {
    'new-idea': 0, 'candidate': 25, 'planned': 50, 'in-progress': 75,
    'backlog': 0, 'done': 100, 'released': 100,
  };
  const releaseProgress = (release: PrioritizeItem): number => {
    if ((release.workProgressMode ?? 'auto') === 'manual') return release.workProgress ?? 0;
    const feats = featuresOfRelease(release.id);
    if (feats.length === 0) return 0;
    const total = feats.reduce((sum, f) => sum + (FEATURE_COMPLETENESS[f.status ?? 'new-idea'] ?? 0), 0);
    return Math.round(total / feats.length);
  };

  const releaseGroups = useMemo(
    () => items.filter(i => i.kind === 'release-group' && (!q || i.name.toLowerCase().includes(q))),
    [items, q],
  );
  const releasesOfGroup = (groupId: string) => items.filter(i => i.kind === 'release' && i.releaseGroupParentId === groupId);

  const parentCandidates = useMemo(
    () => items.filter(i => i.kind === 'product' || i.kind === 'epic' || i.kind === 'initiative' || i.kind === 'release' || i.kind === 'release-group'),
    [items],
  );

  const toggleExpand = (id: string) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    setExpanded(prev => {
      if (prev.size > 0) return prev;
      const n = new Set<string>();
      items.forEach(i => { if (i.kind === 'release') n.add(i.id); });
      return n;
    });
  }, [items.length === 0]);

  const handleSave = (updated: PrioritizeItem) => {
    // Releases/release-groups can't share a name with a sibling of the same kind.
    if (updated.kind === 'release' || updated.kind === 'release-group') {
      const clash = items.some(i => i.kind === updated.kind && i.id !== updated.id && i.name.trim().toLowerCase() === updated.name.trim().toLowerCase());
      if (clash) { alert(`A ${updated.kind === 'release' ? 'release' : 'release group'} with this name already exists. Please choose a unique name.`); return; }
    }
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    setDraft(null);
    prioritizeService.updateItem(updated.id, updated).catch(err => {
      console.error('Failed to save release:', err);
      alert(`Failed to save: ${err?.message ?? err}`);
    });
  };

  const handleDelete = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    setDraft(null);
    prioritizeService.deleteItems([id]).catch(err => {
      console.error('Failed to delete:', err);
      alert(`Failed to delete: ${err?.message ?? err}`);
    });
  };

  const createRelease = async (groupId?: string) => {
    const newItem: PrioritizeItem = {
      id: 'temp_' + uid(), kind: 'release', name: uniqueName('New Release', 'release'),
      ownerId: null, importanceScore: 0, color: '#3b82f6', createdAt: new Date().toISOString(),
      releaseGroupParentId: groupId ?? null,
    };
    try {
      const saved = await prioritizeService.createItem(newItem, items.length);
      setItems(prev => [...prev, saved]);
      if (groupId) setExpanded(prev => new Set(prev).add(groupId));
      setDraft(saved);
    } catch (err) {
      console.error('Failed to create release:', err);
      alert('Failed to create release. Please try again.');
    }
  };

  // Inline-create a named release inside a group (from the group popup).
  // Persists + appends so it shows on BOTH the releases and groups lists,
  // without opening a new modal (the user is mid-edit in the group popup).
  const createReleaseInGroupInline = async (name: string, groupId: string) => {
    const newItem: PrioritizeItem = {
      id: 'temp_' + uid(), kind: 'release', name: uniqueName(name, 'release'),
      ownerId: null, importanceScore: 0, color: '#3b82f6', createdAt: new Date().toISOString(),
      releaseGroupParentId: groupId,
    };
    try {
      const saved = await prioritizeService.createItem(newItem, items.length);
      setItems(prev => [...prev, saved]);
      setExpanded(prev => new Set(prev).add(groupId));
    } catch (err) {
      console.error('Failed to create release:', err);
      alert('Failed to create release. Please try again.');
    }
  };

  const createReleaseGroup = async () => {
    const newItem: PrioritizeItem = {
      id: 'temp_' + uid(), kind: 'release-group', name: uniqueName('Unnamed release group', 'release-group'),
      ownerId: null, importanceScore: 0, color: '#64748b', createdAt: new Date().toISOString(),
    };
    try {
      const saved = await prioritizeService.createItem(newItem, items.length);
      setItems(prev => [...prev, saved]);
      setExpanded(prev => new Set(prev).add(saved.id));
      setDraft(saved);
    } catch (err) {
      console.error('Failed to create release group:', err);
      alert('Failed to create release group. Please try again.');
    }
  };

  // Link / unlink an existing feature to a release (toggle releaseParentId).
  const toggleFeatureInRelease = (featureId: string, releaseId: string, checked: boolean) => {
    const feature = itemById(featureId);
    if (!feature) return;
    const updated = { ...feature, releaseParentId: checked ? releaseId : null };
    setItems(prev => prev.map(i => i.id === featureId ? updated : i));
    setExpanded(prev => new Set(prev).add(releaseId));
    prioritizeService.updateItem(featureId, updated).catch(err => {
      console.error('Failed to link feature:', err);
      alert(`Failed to update: ${err?.message ?? err}`);
    });
  };

  const renderCell = (item: PrioritizeItem, key: ColKey): React.ReactNode => {
    const NA = <span className="prm-not-assigned">Not assigned</span>;
    switch (key) {
      case 'timeframe': {
        const s = formatDateRange(item.startDate, item.endDate);
        return s ? <span style={{ color: '#111827', fontSize: 12 }}>{s}</span> : NA;
      }
      case 'team': {
        if (!item.teams) return <span />;
        return <span className="ini-avatar" style={{ background: '#64748b' }}>{initialsOf(item.teams.split(',')[0])}</span>;
      }
      case 'score': return (item.importanceScore ?? 0) > 0 ? <strong>{item.importanceScore}</strong> : <span className="rel-zero">0</span>;
      case 'owner': {
        const m = memberById(item.ownerId);
        return m ? <span className="ini-avatar" style={{ background: '#EC4899' }} title={m.full_name || m.email || ''}>{initialsOf(m.full_name || m.email)}</span> : <span />;
      }
      case 'hierarchy': {
        // For a feature under a release, show its real component/product parent
        // (not the release). For a release, show its release group.
        const parentId = item.kind === 'feature'
          ? (item.parentId || item.productParentId)
          : item.releaseGroupParentId;
        if (!parentId) return NA;
        const p = itemById(parentId);
        if (!p) return NA;
        const icon = p.kind === 'release' ? <FlagMarker color={p.color} />
          : p.kind === 'release-group' ? <ReleaseGroupSymbol size={13} />
          : p.kind === 'product' ? <ProductIcon /> : <ComponentIcon />;
        return <span className="prm-list-hierarchy">{icon}<span>{p.name}</span></span>;
      }
      case 'status': {
        if (item.kind === 'release') {
          const rc = STATUS_PILL_STYLE[item.status || 'upcoming'];
          return <span className="prm-list-pill" style={{ background: rc.bg, color: rc.fg }}><span className="prm-list-pill-bar" style={{ background: rc.fg }} />{rc.label}</span>;
        }
        if (!item.status) return NA;
        const c = STATUS_PILL_STYLE[item.status];
        return <span className="prm-list-pill" style={{ background: c.bg, color: c.fg }}><span className="prm-list-pill-bar" style={{ background: c.fg }} />{c.label}</span>;
      }
      case 'health': {
        if (item.kind !== 'feature') return NA;
        // Prefer the latest health update entry; fall back to the scalar field.
        const updates = item.healthUpdates ?? [];
        const latest = updates.length ? updates[updates.length - 1] : null;
        const state: HealthState = latest
          ? (HEALTH_ICON_TO_STATE[latest.status] ?? 'none')
          : (item.health || 'none');
        if (state === 'none') return NA;
        const h = HEALTH_STYLE[state];
        return <span className="prm-list-health"><span className="prm-list-health-icon" style={{ background: h.color }} /><span>{h.label}</span>{latest?.at && <span className="prm-list-health-ago">{latest.at}</span>}</span>;
      }
      case 'progress': {
        if (item.kind !== 'feature' && item.kind !== 'release') return <span />;
        const pct = item.kind === 'release' ? releaseProgress(item) : (item.workProgress ?? 0);
        return <span className="prm-list-progress"><span className="prm-list-progress-track"><span className="prm-list-progress-fill" style={{ width: `${pct}%` }} /></span><span className="prm-list-progress-num">{pct}%</span></span>;
      }
    }
  };

  const renderRow = (item: PrioritizeItem, depth: number, isRelease: boolean) => {
    const children = isRelease ? featuresOfRelease(item.id) : [];
    const canExpand = children.length > 0;
    const isOpen = expanded.has(item.id);
    const indent = depth * 22;
    return (
      <React.Fragment key={item.id}>
        <tr className="prm-row" onClick={() => setDraft(item)}>
          <td className="prm-cell prm-cell-name">
            <div className="prm-cell-name-inner" style={{ paddingLeft: 8 + indent }}>
              <button className={`prm-expand-btn ${isOpen ? 'prm-expand-btn-open' : ''}`} onClick={e => { e.stopPropagation(); if (canExpand) toggleExpand(item.id); }} style={{ visibility: canExpand ? 'visible' : 'hidden' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              {isRelease ? <FlagMarker color={item.color || '#f59e0b'} /> : <SquareMarker color={item.color || 'var(--brand-500)'} />}
              <span className={`prm-row-name ${isRelease ? 'ini-name-bold' : ''}`}>{item.name}</span>
              {isRelease && canManage && (
                <button
                  className="ini-add-sub"
                  title="Add feature"
                  onClick={e => {
                    e.stopPropagation();
                    const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                    setPickerFor({ id: item.id, top: r.bottom + 4, left: r.left });
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              )}
            </div>
          </td>
          {visibleReleaseCols.map(col => (
            <td key={col.key} className="prm-cell" style={{ width: col.width, minWidth: col.width }}>{renderCell(item, col.key)}</td>
          ))}
        </tr>
        {isRelease && isOpen && children.map(c => renderRow(c, depth + 1, false))}
      </React.Fragment>
    );
  };

  // Release-groups view: group → its releases → "Create release" footer row.
  const renderGroupRow = (group: PrioritizeItem) => {
    const groupReleases = releasesOfGroup(group.id);
    const isOpen = expanded.has(group.id);
    return (
      <React.Fragment key={group.id}>
        <tr className="prm-row" onClick={() => setDraft(group)}>
          <td className="prm-cell prm-cell-name">
            <div className="prm-cell-name-inner" style={{ paddingLeft: 8 }}>
              <button className={`prm-expand-btn ${isOpen ? 'prm-expand-btn-open' : ''}`} onClick={e => { e.stopPropagation(); toggleExpand(group.id); }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <ReleaseGroupSymbol size={15} />
              <span className="prm-row-name ini-name-bold">{group.name}</span>
            </div>
          </td>
          {GROUP_COLUMNS.map(col => <td key={col.key} className="prm-cell" style={{ width: col.width, minWidth: col.width }}>{renderCell(group, col.key)}</td>)}
        </tr>
        {isOpen && groupReleases.map(r => (
          <tr key={r.id} className="prm-row" onClick={() => setDraft(r)}>
            <td className="prm-cell prm-cell-name">
              <div className="prm-cell-name-inner" style={{ paddingLeft: 38 }}>
                <FlagMarker color={r.color || '#3b82f6'} />
                <span className="prm-row-name">{r.name}</span>
              </div>
            </td>
            {GROUP_COLUMNS.map(col => <td key={col.key} className="prm-cell" style={{ width: col.width, minWidth: col.width }}>{renderCell(r, col.key)}</td>)}
          </tr>
        ))}
        {isOpen && canManage && (
          <tr className="prm-row rel-group-create-sub" onClick={e => { e.stopPropagation(); createRelease(group.id); }}>
            <td className="prm-cell prm-cell-name" colSpan={GROUP_COLUMNS.length + 1}>
              <div className="prm-cell-name-inner" style={{ paddingLeft: 38 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                <span style={{ color: '#9ca3af', fontSize: 13 }}>Create release</span>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <div className="insights-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'releases'}
              className={`insights-tab ${tab === 'releases' ? 'active' : ''}`}
              onClick={() => setTab('releases')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill={tab === 'releases' ? '#4338ca' : '#6b7280'} stroke="none"><path d="M5 3v18l7-5 7 5V3z"/></svg>
              <span>Releases</span>
              <span className="insights-tab-count">{releases.length}</span>
            </button>
            <button
              role="tab"
              aria-selected={tab === 'groups'}
              className={`insights-tab ${tab === 'groups' ? 'active' : ''}`}
              onClick={() => setTab('groups')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={tab === 'groups' ? '#4338ca' : '#6b7280'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="3" width="13" height="13" rx="2"/><path d="M16 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h3"/></svg>
              <span>Release groups</span>
              <span className="insights-tab-count">{releaseGroups.length}</span>
            </button>
          </div>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder={tab === 'releases' ? 'Search releases…' : 'Search release groups…'} value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
            </div>
            {tab === 'releases' && (
              <BoardControlsButton columns={COLUMNS.map(c => ({ key: c.key, label: c.label }))} storageKey="releases:boardControls" prefs={boardPrefs} onChange={setBoardPrefs} />
            )}
            {canManage && (
              tab === 'releases' ? (
                <button className="customers-add-btn btn-primary-cta" onClick={() => createRelease()} title="Add a new release">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Release
                </button>
              ) : (
                <button className="customers-add-btn btn-primary-cta" onClick={createReleaseGroup} title="Add a new release group">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Create release group
                </button>
              )
            )}
          </div>
        </div>

        <div className="prm-list-wrap">
          <div className="prm-list-table-wrap">
            <table className="prm-list-table">
              <colgroup>
                <col style={{ width: 420, minWidth: 280 }} />
                {activeColumns.map(c => <col key={c.key} style={{ width: c.width, minWidth: c.width }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th className="prm-th prm-th-fixed"><span className="prm-th-label">{tab === 'releases' ? 'Releases, Features' : 'Release groups'}</span></th>
                  {activeColumns.map(c => <th key={c.key} className="prm-th"><span className="prm-th-label">{c.label}</span></th>)}
                </tr>
              </thead>
              <tbody>
                {tab === 'releases' ? (
                  <>
                    {releases.length === 0 && <tr><td colSpan={activeColumns.length + 1} className="prm-empty">No releases yet</td></tr>}
                    {releases.map(r => renderRow(r, 0, true))}
                  </>
                ) : (
                  <>
                    {releaseGroups.length === 0 && <tr><td colSpan={GROUP_COLUMNS.length + 1} className="prm-empty">No release groups yet</td></tr>}
                    {releaseGroups.map(g => renderGroupRow(g))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {canManage && (
            tab === 'releases' ? (
              <button className="ini-create-row" onClick={() => createRelease()}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                Create release
              </button>
            ) : (
              <button className="ini-create-row" onClick={createReleaseGroup}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                Create release group
              </button>
            )
          )}
        </div>

        {pickerFor && (
          <div className="rel-pick-anchor" style={{ top: pickerFor.top, left: pickerFor.left }}>
            <AddLinkedFeaturePopup
              items={items}
              parentId={pickerFor.id}
              linkField="releaseParentId"
              onToggle={(fid, checked) => toggleFeatureInRelease(fid, pickerFor.id, checked)}
              onClose={() => setPickerFor(null)}
            />
          </div>
        )}

        {draft && (
          <PrioritizeDetailModal
            item={draft}
            parentCandidates={parentCandidates}
            members={members}
            allItems={items}
            companies={companies}
            customerUsers={customerUsers}
            documents={documents}
            onCreateRelease={createReleaseInGroupInline}
            onDelete={handleDelete}
            onToggleFeature={(fid, checked) => toggleFeatureInRelease(fid, draft.id, checked)}
            onClose={() => setDraft(null)}
            onSave={handleSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default Releases;
